import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, FileText, KeyRound, Check } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "/assets/community/lagoon-city-main.webp";

const CHECKLIST = [
  "Private sandy beach perfect for families",
  "Every home has access to beautiful Lake Simcoe",
  "Private boat dockage right at your doorstep",
  "Full-service marina for boating enthusiasts",
  "Great local restaurants nearby",
  "Amazing snowmobile trails in the winter",
  "Only 15 minutes from Casino Rama",
  "Within 90 minutes of the GTA",
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (href) => scrollToId(href);

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onTiltMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 8, ry: px * 10 });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24">
        <img src={HERO_IMG} alt="Lagoon City waterfront from above" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/60 to-[#0D0B0E]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0B0E]/85 via-[#0D0B0E]/30 to-transparent" />
        <div className="absolute inset-0 hairline-grid opacity-60" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-44 pb-20">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-14 items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-8"
              data-testid="hero-eyebrow"
            >
              Joselle Alves · Broker · Lagoon City
            </motion.p>

            <h1
              data-testid="hero-headline"
              className="font-display font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight"
            >
              <MaskedLine delay={0.25}>Ontario&apos;s</MaskedLine>
              <MaskedLine delay={0.4}>best kept</MaskedLine>
              <MaskedLine delay={0.55}>
                <em className="text-[#E890A7] font-display italic">secret.</em>
              </MaskedLine>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-properties"
                onClick={() => scrollTo("#properties")}
                className="group rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
              >
                <KeyRound size={16} />
                Lagoon City Waterfront Homes For Sale
              </button>
              <button
                data-testid="hero-cta-newsletter"
                onClick={() => scrollTo("#newsletter")}
                className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3.5 flex items-center gap-2 hover:bg-[#E890A7]/10 hover:border-[#E890A7]/70 transition-all duration-300"
              >
                <FileText size={16} />
                This Month&apos;s Market Report
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="hidden lg:block"
          >
            <div
              data-testid="hero-showcase-card"
              onMouseMove={onTiltMove}
              onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
              style={{
                transform: `perspective(900px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                transition: "transform 0.25s ease-out",
              }}
              className="relative rounded-3xl overflow-hidden border border-[#E890A7]/20 bg-[#151218]/70 backdrop-blur-xl rose-glow"
            >
              <img
                src="/assets/listings/l2.jpg"
                alt="9 - 100 Laguna Parkway, Lagoon City"
                className="h-56 w-full object-cover"
              />
              <div className="p-5">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]">Just Listed · Laguna Parkway</p>
                <p className="font-display text-2xl mt-1">$525,000</p>
                <p className="text-xs text-slate-400 mt-1">3 bd · 4 ba · 1,800–1,999 sq ft</p>
              </div>
              <div className="absolute top-4 right-4 animate-float-slow bg-[#0D0B0E]/80 backdrop-blur-md border border-[#E890A7]/30 rounded-full px-4 py-1.5 text-[10px] font-mono tracking-widest uppercase text-[#F2A7BA]">
                Waterfront Specialist
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-discover-block"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1 }}
          className="mt-24 rounded-[2rem] border border-[#E890A7]/15 bg-[#0D0B0E]/70 backdrop-blur-xl p-8 sm:p-12"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7] mb-4">
            Discover Lagoon City
          </p>
          <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Ontario&apos;s hidden <em className="italic text-[#E890A7]">waterfront paradise</em>
          </h2>

          <div className="grid lg:grid-cols-2 gap-10 mt-8">
            <div className="space-y-5 text-slate-300/90 font-light leading-relaxed text-sm sm:text-base">
              <p>
                A one-of-a-kind waterfront community nestled along the eastern shore of Lake Simcoe,
                Ontario. Often called the <strong className="font-semibold text-slate-100">&ldquo;Venice of the North,&rdquo;</strong> Lagoon City offers
                something truly special: <strong className="font-semibold text-slate-100">step out your back door, hop in your boat or canoe, and
                you&apos;re on the water.</strong>
              </p>
              <p>
                With more than 10 miles of interconnected lagoons and direct access to Lake Simcoe,
                the water is woven into everyday life here. Spend the morning boating, fishing or
                paddling, enjoy a leisurely afternoon on the lake, or simply relax and watch the
                boats go by from your own backyard.
              </p>
              <p>
                Beyond the waterways, Lagoon City offers beautiful homes, beaches, trails and a
                welcoming year-round community. It&apos;s a place where you can enjoy the relaxed
                feel of cottage country while still having the comforts of a residential community.
              </p>
              <p>
                Whether you&apos;re looking for a weekend getaway, a retirement home, or a wonderful
                place to raise a family, <strong className="font-semibold text-slate-100">Lagoon City isn&apos;t just a place to live — it&apos;s a
                lifestyle built around the water.</strong>
              </p>
              <p className="text-[#F2A7BA] font-display italic text-xl">
                Whether you&apos;re raising a family,
                retiring, or simply looking for a peaceful getaway, this community has it all.
              </p>
            </div>

            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4 content-start" data-testid="hero-checklist">
              {CHECKLIST.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-[#E890A7]/15 border border-[#E890A7]/40 flex items-center justify-center">
                    <Check size={11} className="text-[#E890A7]" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-10 text-slate-400 font-light leading-relaxed text-sm sm:text-base max-w-3xl">
            From boating and swimming in the summer to snowmobiling in the winter, Lagoon City
            offers the ultimate four-season lifestyle with breathtaking scenery all year long. Once
            you experience it, you&apos;ll never want to leave.
          </p>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("#properties")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-slate-400 hover:text-[#E890A7] transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2 }}
        aria-label="Scroll down"
      >
        <ArrowDown size={20} />
      </motion.button>
    </section>
  );
}
